import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  executeBolusProcedure,
  normalizeReadingRows,
  type GlucoseReadingPoint,
} from '../services/analyticsApi';
import './LiveGlucoseChart.css';

/** Sub–1h windows: if the whole range fits in this width at reference density, we only expand to this minimum. */
const MIN_INNER_PLOT_WIDTH = 720;

const SLOTS_PER_HOUR = 60 / 5;

/** Pixels per 5-minute column for the visible viewport (matches a 1h window at MIN_INNER_PLOT_WIDTH). */
export const LIVE_CHART_PX_PER_FIVE_MIN = MIN_INNER_PLOT_WIDTH / SLOTS_PER_HOUR;

/**
 * Scrollable track extends past the viewport by this many pixels; scroll position maps linearly to time.
 * Keeps DOM size bounded for any selected window (e.g. 12 months) while panning stays smooth.
 */
const EXTRA_SCROLL_PX = 4000;

/** Don’t draw a separate SVG line per 5 min when the visible window would contain too many. */
const MAX_FIVE_MIN_GRID_LINES = 576;

/** Server fetch window — matches iOS-style windowed loads; raw ~5-min CGM points. */
const CHUNK_MS = 4 * 60 * 60 * 1000;

/** Prefetch this many 4h chunks beyond the visible time range (each side). */
const BUFFER_CHUNK_COUNT = 3;

const LEFT_PAD = 44;
const RIGHT_PAD = 12;
const TOP_PAD = 14;
const BOTTOM_PAD = 28;
const FIVE_MIN_MS = 5 * 60 * 1000;

export type LiveProfileSeriesMeta = { id: string; name: string; color: string };

type ExtentState = {
  status: 'idle' | 'loading' | 'ready' | 'error';
  /** Min of per-user earliest readings; null if no profile has CGM rows. */
  globalEarliestMs: number | null;
};

function parseExtentRow(row: Record<string, unknown> | undefined): {
  earliestMs: number | null;
  latestMs: number | null;
} {
  if (!row) return { earliestMs: null, latestMs: null };
  const pick = (keys: string[]): number | null => {
    for (const k of keys) {
      const v = row[k];
      if (v == null || v === '') continue;
      const t = new Date(String(v)).getTime();
      if (!Number.isNaN(t)) return t;
    }
    return null;
  };
  return {
    earliestMs: pick(['earliest_ts', 'earliestTs', 'earliest']),
    latestMs: pick(['latest_ts', 'latestTs', 'latest']),
  };
}

function chunkIndicesOverlapping(
  rangeStart: number,
  rangeEnd: number,
  windowStart: number,
  windowEnd: number,
  chunkMs: number
): number[] {
  if (rangeEnd <= windowStart || rangeStart >= windowEnd) return [];
  const n = Math.max(1, Math.ceil((windowEnd - windowStart) / chunkMs));
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const cs = windowStart + i * chunkMs;
    const ce = Math.min(cs + chunkMs, windowEnd);
    if (ce > rangeStart && cs < rangeEnd) out.push(i);
  }
  return out;
}

function mergeReadings(existing: GlucoseReadingPoint[], incoming: GlucoseReadingPoint[]): GlucoseReadingPoint[] {
  const map = new Map<string, GlucoseReadingPoint>();
  for (const r of existing) map.set(r.timestamp, r);
  for (const r of incoming) map.set(r.timestamp, r);
  return Array.from(map.values()).sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
}

type Props = {
  profileMeta: LiveProfileSeriesMeta[];
  liveHours: number;
};

const LiveGlucoseChart: React.FC<Props> = ({ profileMeta, liveHours }) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ active: boolean; startX: number; startScroll: number }>({
    active: false,
    startX: 0,
    startScroll: 0,
  });
  const correctingScrollRef = useRef(false);

  const [readingsByUser, setReadingsByUser] = useState<Record<string, GlucoseReadingPoint[]>>({});
  const loadedChunksRef = useRef<Set<number>>(new Set());
  const loadingChunksRef = useRef<Set<number>>(new Set());
  const lastScrollLeftRef = useRef<number | undefined>(undefined);
  const [loadingCount, setLoadingCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const [extent, setExtent] = useState<ExtentState>({
    status: 'idle',
    globalEarliestMs: null,
  });

  const [hoverX, setHoverX] = useState<number | null>(null);

  const [plotWidth, setPlotWidth] = useState(0);
  const [viewStartMs, setViewStartMs] = useState(() => Date.now() - 3 * 3600000);
  const [viewEndMs, setViewEndMs] = useState(() => Date.now());

  /** Rolling “now” for the window; refresh window length when parent mounts, and step forward automatically. */
  const [rangeEndMs, setRangeEndMs] = useState(() => Date.now());
  useEffect(() => {
    setRangeEndMs(Date.now());
    const t = setInterval(() => {
      const now = Date.now();
      setRangeEndMs(now);
      
      // Invalidate the chunk that contains "now" so new Dexcom data gets fetched
      const ts = boundsRef.current.timelineStartMs;
      if (typeof ts === 'number') {
        const currentIdx = Math.floor((now - ts) / CHUNK_MS);
        loadedChunksRef.current.delete(currentIdx);
      }
    }, 60000);
    return () => clearInterval(t);
  }, [liveHours]);

  const fullEnd = rangeEndMs;
  const fullStart = fullEnd - liveHours * 3600000;

  const profileKey = useMemo(() => profileMeta.map((p) => p.id).sort().join(','), [profileMeta]);

  const totalPointsLoaded = useMemo(
    () => profileMeta.reduce((n, p) => n + (readingsByUser[p.id]?.length ?? 0), 0),
    [profileMeta, readingsByUser]
  );

  /**
   * Left edge of the pannable chart — only uses server extent so it doesn’t thrash when new chunks arrive.
   * If extent fails to load, we keep the requested window start and rely on messaging / prefetch.
   */
  const timelineStartMs = useMemo(() => {
    if (extent.status === 'ready' && extent.globalEarliestMs != null) {
      return Math.max(fullStart, extent.globalEarliestMs);
    }
    return fullStart;
  }, [fullStart, extent.status, extent.globalEarliestMs]);

  const extentResetKey = `${extent.status}-${extent.globalEarliestMs ?? 'na'}`;

  const timelineEndMs = fullEnd;
  const spanMs = Math.max(timelineEndMs - timelineStartMs, FIVE_MIN_MS);

  const boundsRef = useRef({ timelineStartMs, timelineEndMs });
  boundsRef.current = { timelineStartMs, timelineEndMs };

  const effectivePlotW = plotWidth > 40 ? plotWidth : MIN_INNER_PLOT_WIDTH;
  const innerW = Math.max(effectivePlotW - RIGHT_PAD, 240);

  const visibleDurationMs = useMemo(
    () => Math.max((innerW / LIVE_CHART_PX_PER_FIVE_MIN) * FIVE_MIN_MS, FIVE_MIN_MS),
    [innerW]
  );

  const scrollContentWidth = useMemo(() => {
    if (plotWidth < 40) return MIN_INNER_PLOT_WIDTH;
    if (spanMs <= visibleDurationMs) return plotWidth;
    return plotWidth + EXTRA_SCROLL_PX;
  }, [plotWidth, spanMs, visibleDurationMs]);

  const h = 220;

  const series = useMemo(
    () =>
      profileMeta.map((m) => ({
        name: m.name,
        color: m.color,
        readings: readingsByUser[m.id] ?? [],
      })),
    [profileMeta, readingsByUser]
  );

  /** Min / max CGM timestamps for all selected profiles (server). */
  useEffect(() => {
    if (profileMeta.length === 0) {
      setExtent({ status: 'ready', globalEarliestMs: null });
      return;
    }
    let cancelled = false;
    setExtent((s) => ({ ...s, status: 'loading' }));
    (async () => {
      try {
        const earlies: number[] = [];
        for (const m of profileMeta) {
          const res = await executeBolusProcedure('sp_glucose_readings_extent', { userId: m.id });
          const row = (res.rows?.[0] ?? undefined) as Record<string, unknown> | undefined;
          const { earliestMs } = parseExtentRow(row);
          if (earliestMs != null) earlies.push(earliestMs);
        }
        if (cancelled) return;
        setExtent({
          status: 'ready',
          globalEarliestMs: earlies.length > 0 ? Math.min(...earlies) : null,
        });
      } catch {
        if (cancelled) return;
        setExtent({ status: 'error', globalEarliestMs: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profileKey, profileMeta]);

  const fetchChunk = useCallback(
    async (chunkIndex: number) => {
      if (loadedChunksRef.current.has(chunkIndex) || loadingChunksRef.current.has(chunkIndex)) return;

      const { timelineStartMs: ts, timelineEndMs: te } = boundsRef.current;
      const chunkStart = ts + chunkIndex * CHUNK_MS;
      const chunkEnd = Math.min(chunkStart + CHUNK_MS, te);
      if (chunkEnd <= chunkStart) return;

      loadingChunksRef.current.add(chunkIndex);
      setLoadingCount((c) => c + 1);
      setError(null);

      const startIso = new Date(chunkStart).toISOString();
      const endIso = new Date(chunkEnd).toISOString();

      try {
        const results = await Promise.all(
          profileMeta.map(async (m) => {
            const res = await executeBolusProcedure('sp_glucose_readings', {
              startDate: startIso,
              endDate: endIso,
              aggregation: 'raw',
              userId: m.id,
            });
            const rows = normalizeReadingRows((res.rows || []) as Record<string, unknown>[]);
            return [m.id, rows] as const;
          })
        );

        setReadingsByUser((prev) => {
          const next = { ...prev };
          for (const [userId, rows] of results) {
            next[userId] = mergeReadings(next[userId] ?? [], rows);
          }
          return next;
        });
        loadedChunksRef.current.add(chunkIndex);
      } catch (e: unknown) {
        const msg =
          e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
            : null;
        setError(msg || (e instanceof Error ? e.message : 'Failed to load readings'));
      } finally {
        loadingChunksRef.current.delete(chunkIndex);
        setLoadingCount((c) => Math.max(0, c - 1));
      }
    },
    [profileMeta]
  );

  const ensureChunksForVisibleRange = useCallback(
    (
      rangeStartMs: number,
      rangeEndMs: number,
      opts?: { pastExtraMs?: number; futureExtraMs?: number }
    ) => {
      const { timelineStartMs: ts, timelineEndMs: te } = boundsRef.current;
      const buf = BUFFER_CHUNK_COUNT * CHUNK_MS;
      const past = buf + (opts?.pastExtraMs ?? 0);
      const future = buf + (opts?.futureExtraMs ?? 0);
      const indices = chunkIndicesOverlapping(
        rangeStartMs - past,
        rangeEndMs + future,
        ts,
        te,
        CHUNK_MS
      );
      for (const idx of indices) {
        void fetchChunk(idx);
      }
    },
    [fetchChunk]
  );

  const applyScrollAndPrefetch = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;

    const { timelineStartMs: ts, timelineEndMs: te } = boundsRef.current;
    const pw = el.clientWidth ?? plotWidth;
    if (pw < 40) return;

    const inner = Math.max(pw - RIGHT_PAD, 240);
    const visDur = Math.max((inner / LIVE_CHART_PX_PER_FIVE_MIN) * FIVE_MIN_MS, FIVE_MIN_MS);
    const sp = Math.max(te - ts, FIVE_MIN_MS);
    const scw = sp <= visDur ? pw : pw + EXTRA_SCROLL_PX;
    const maxScroll = Math.max(0, scw - pw);
    const sl = el.scrollLeft;

    let vStart: number;
    let vEnd: number;
    if (sp <= visDur) {
      vStart = ts;
      vEnd = te;
    } else {
      const rawVStart = ts + (maxScroll <= 0 ? 0 : (sl / maxScroll) * (sp - visDur));
      vStart = Math.max(ts, Math.min(rawVStart, te - visDur));
      vEnd = Math.min(vStart + visDur, te);

      if (maxScroll > 0 && sp > visDur && !correctingScrollRef.current) {
        const expectedSl = ((vStart - ts) / (sp - visDur)) * maxScroll;
        if (Math.abs(expectedSl - sl) > 2) {
          correctingScrollRef.current = true;
          el.scrollLeft = expectedSl;
          requestAnimationFrame(() => {
            correctingScrollRef.current = false;
          });
        }
      }
    }

    setViewStartMs((prev) => (prev === vStart ? prev : vStart));
    setViewEndMs((prev) => (prev === vEnd ? prev : vEnd));

    const prevSl = lastScrollLeftRef.current;
    lastScrollLeftRef.current = el.scrollLeft;

    let pastExtraMs = 0;
    let futureExtraMs = 0;
    if (prevSl !== undefined) {
      if (el.scrollLeft < prevSl - 2) pastExtraMs = 2 * CHUNK_MS;
      if (el.scrollLeft > prevSl + 2) futureExtraMs = 2 * CHUNK_MS;
    }
    ensureChunksForVisibleRange(vStart, vEnd, { pastExtraMs, futureExtraMs });
  }, [plotWidth, ensureChunksForVisibleRange]);

  const applyScrollRef = useRef(applyScrollAndPrefetch);
  applyScrollRef.current = applyScrollAndPrefetch;

  useLayoutEffect(() => {
    applyScrollRef.current();
  }, [scrollContentWidth, plotWidth, timelineStartMs, timelineEndMs]);

  /** Reset loaded chunks when window, profiles, or data-backed timeline start changes. */
  useEffect(() => {
    loadedChunksRef.current = new Set();
    loadingChunksRef.current = new Set();
    lastScrollLeftRef.current = undefined;
    setReadingsByUser({});
    setError(null);
  }, [fullStart, fullEnd, profileKey, extentResetKey]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const run = () => {
      window.requestAnimationFrame(() => applyScrollRef.current());
    };

    const onScroll = () => run();

    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(() => {
      setPlotWidth(el.clientWidth);
      run();
    });
    ro.observe(el);
    setPlotWidth(el.clientWidth);
    run();

    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const t = window.requestAnimationFrame(() => {
      el.scrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      applyScrollRef.current();
    });
    return () => cancelAnimationFrame(t);
  }, [fullStart, fullEnd, profileKey, liveHours, scrollContentWidth, timelineStartMs]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const onDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      dragRef.current = { active: true, startX: e.clientX, startScroll: el.scrollLeft };
      el.classList.add('live-glucose-viewport--grabbing');
    };
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current.active) return;
      el.scrollLeft = dragRef.current.startScroll - (e.clientX - dragRef.current.startX);
    };
    const onUp = () => {
      dragRef.current.active = false;
      el.classList.remove('live-glucose-viewport--grabbing');
    };

    el.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      el.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const viewSpanMs = Math.max(viewEndMs - viewStartMs, FIVE_MIN_MS);
  const slotCountVisible = viewSpanMs / FIVE_MIN_MS;

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x >= 0 && x <= innerW) {
      setHoverX(x);
    } else {
      setHoverX(null);
    }
  }, [innerW]);

  const handleMouseLeave = useCallback(() => {
    setHoverX(null);
  }, []);

  const hoverData = useMemo(() => {
    if (hoverX === null) return null;
    const hoverMs = viewStartMs + (hoverX / innerW) * viewSpanMs;
    const points: { color: string; value: number; name: string }[] = [];
    let closestMs = hoverMs;
    let minDiff = Infinity;

    for (const s of series) {
      const inWindow = s.readings.filter((r) => {
        const t = new Date(r.timestamp).getTime();
        return t >= viewStartMs && t <= viewEndMs;
      });
      if (inWindow.length === 0) continue;
      
      // Find closest
      let closest = inWindow[0];
      let bestDiff = Math.abs(new Date(closest.timestamp).getTime() - hoverMs);
      
      for (const r of inWindow) {
        const diff = Math.abs(new Date(r.timestamp).getTime() - hoverMs);
        if (diff < bestDiff) {
          bestDiff = diff;
          closest = r;
        }
      }
      
      if (bestDiff < FIVE_MIN_MS * 3) { // Only show if within 15 mins
        points.push({ color: s.color, value: Math.round(closest.glucose_value), name: s.name });
        if (bestDiff < minDiff) {
          minDiff = bestDiff;
          closestMs = new Date(closest.timestamp).getTime();
        }
      }
    }
    
    if (points.length === 0) return null;
    return {
      x: ((closestMs - viewStartMs) / viewSpanMs) * innerW,
      timeMsg: new Date(closestMs).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
      points
    };
  }, [hoverX, viewStartMs, innerW, viewSpanMs, series, viewEndMs]);

  const emptyOverlayMessage = useMemo(() => {
    if (extent.status === 'loading' && totalPointsLoaded === 0) {
      return 'Loading CGM history bounds…';
    }
    if (extent.status === 'ready' && extent.globalEarliestMs == null && totalPointsLoaded === 0 && loadingCount === 0) {
      return 'No CGM readings on file for the selected profile(s).';
    }
    if (totalPointsLoaded > 0 && extent.status === 'ready' && extent.globalEarliestMs != null) {
      const anchor = extent.globalEarliestMs;
      if (viewEndMs < anchor) {
        return `No data before ${new Date(anchor).toLocaleDateString(undefined, { dateStyle: 'medium' })} — the chart starts at the earliest CGM reading.`;
      }
    }
    if (totalPointsLoaded > 0) {
      return 'No CGM readings in this part of the timeline (gap or still loading adjacent slices).';
    }
    if (loadingCount > 0) {
      return 'Loading readings…';
    }
    if (extent.status === 'error') {
      return 'Could not load CGM date range from the server — pan to load readings, or ensure sp_glucose_readings_extent is deployed.';
    }
    return 'No CGM readings in this view yet.';
  }, [
    extent.status,
    extent.globalEarliestMs,
    totalPointsLoaded,
    loadingCount,
    viewEndMs,
  ]);

  const { minG, maxG, paths, fiveMinGridTicks, labelTicks } = useMemo(() => {
    const xAt = (tMs: number) => ((tMs - viewStartMs) / viewSpanMs) * innerW;

    const all = series.flatMap((s) =>
      s.readings.filter((r) => {
        const t = new Date(r.timestamp).getTime();
        return t >= viewStartMs && t <= viewEndMs;
      })
    );
    if (all.length === 0) {
      return {
        minG: 40,
        maxG: 300,
        paths: [] as { d: string; color: string; name: string }[],
        fiveMinGridTicks: [] as number[],
        labelTicks: [] as number[],
      };
    }
    const values = all.map((r) => r.glucose_value);
    const minG = Math.max(40, Math.min(...values) - 20);
    const maxG = Math.min(400, Math.max(...values) + 40);
    const gSpan = Math.max(maxG - minG, 1);

    const padMs = viewSpanMs * 0.02;
    const drawStart = viewStartMs - padMs;
    const drawEnd = viewEndMs + padMs;

    const pathList = series.map((s) => {
      const inWindow = s.readings.filter((r) => {
        const t = new Date(r.timestamp).getTime();
        return t >= drawStart && t <= drawEnd;
      });
      if (inWindow.length < 2) return { d: '', color: s.color, name: s.name };
      const pts = inWindow.map((r) => {
        const t = new Date(r.timestamp).getTime();
        const x = xAt(t);
        const y = h - BOTTOM_PAD - ((r.glucose_value - minG) / gSpan) * (h - TOP_PAD - BOTTOM_PAD);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      });
      return { d: `M ${pts.join(' L ')}`, color: s.color, name: s.name };
    });

    const hourMs = 60 * 60 * 1000;

    const fiveMinGridTicks: number[] = [];
    if (slotCountVisible <= MAX_FIVE_MIN_GRID_LINES) {
      let t = Math.ceil(viewStartMs / FIVE_MIN_MS) * FIVE_MIN_MS;
      for (; t <= viewEndMs; t += FIVE_MIN_MS) {
        if (t >= viewStartMs) fiveMinGridTicks.push(t);
      }
    } else {
      let t = Math.ceil(viewStartMs / hourMs) * hourMs;
      for (; t <= viewEndMs; t += hourMs) {
        if (t >= viewStartMs) fiveMinGridTicks.push(t);
      }
    }

    let labelStep = FIVE_MIN_MS;
    if (viewSpanMs > 3 * hourMs) labelStep = 30 * 60 * 1000;
    if (viewSpanMs > 48 * hourMs) labelStep = 2 * hourMs;
    if (viewSpanMs > 14 * 24 * hourMs) labelStep = 6 * hourMs;
    if (viewSpanMs > 90 * 24 * hourMs) labelStep = 24 * hourMs;

    const labelTicks: number[] = [];
    const firstLabel = Math.ceil(viewStartMs / labelStep) * labelStep;
    for (let t = firstLabel; t <= viewEndMs; t += labelStep) {
      labelTicks.push(t);
    }

    return { minG, maxG, paths: pathList, fiveMinGridTicks, labelTicks };
  }, [series, viewStartMs, viewEndMs, viewSpanMs, innerW, h, slotCountVisible]);

  const xAtMs = useCallback(
    (tMs: number) => ((tMs - viewStartMs) / viewSpanMs) * innerW,
    [viewStartMs, viewSpanMs, innerW]
  );

  const anyLine = paths.some((p) => p.d);

  const formatTick = (tMs: number) => {
    const d = new Date(tMs);
    if (viewSpanMs <= 3 * 60 * 60 * 1000) {
      return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    }
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const showEmptyOverlay = !anyLine && loadingCount === 0 && !error;

  return (
    <div className="live-glucose-multi-wrap live-glucose-paged">
      {loadingCount > 0 && <div className="live-glucose-loading-badge" aria-live="polite" />}
      {error && <div className="analytics-error live-glucose-error">{error}</div>}

      <div className="live-glucose-chart-frame">
        <div className="live-glucose-split">
          <div className="live-glucose-y-rail">
            <svg width={LEFT_PAD} height={h} className="live-glucose-y-svg" aria-hidden="true">
              <line
                x1={LEFT_PAD - 1}
                y1={TOP_PAD}
                x2={LEFT_PAD - 1}
                y2={h - BOTTOM_PAD}
                stroke="var(--border-light)"
                strokeWidth={1}
              />
              <text x={4} y={TOP_PAD + 8} className="analytics-svg-label">
                {Math.round(maxG)}
              </text>
              <text x={4} y={h - BOTTOM_PAD + 4} className="analytics-svg-label">
                {Math.round(minG)}
              </text>
            </svg>
          </div>
          <div className="live-glucose-plot-shell">
            <div className="live-glucose-viewport" ref={viewportRef}>
              <div
                className="live-glucose-scroll-spacer"
                style={{ width: scrollContentWidth, height: h }}
                aria-hidden="true"
              />
            </div>
            <div className="live-glucose-plot-overlay">
              {showEmptyOverlay ? (
                <div className="analytics-chart-empty live-glucose-empty-overlay">{emptyOverlayMessage}</div>
              ) : (
                <svg
                  className="analytics-line-svg live-glucose-viewport-svg"
                  width={effectivePlotW}
                  height={h}
                  viewBox={`0 0 ${effectivePlotW} ${h}`}
                  preserveAspectRatio="xMinYMid meet"
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  style={{ cursor: 'crosshair' }}
                >
                  <line
                    x1={0}
                    y1={h - BOTTOM_PAD}
                    x2={innerW}
                    y2={h - BOTTOM_PAD}
                    stroke="var(--border-light)"
                    strokeWidth={1}
                  />
                  <line
                    x1={0}
                    x2={innerW}
                    y1={h - BOTTOM_PAD - ((180 - minG) / (maxG - minG)) * (h - TOP_PAD - BOTTOM_PAD)}
                    y2={h - BOTTOM_PAD - ((180 - minG) / (maxG - minG)) * (h - TOP_PAD - BOTTOM_PAD)}
                    stroke="rgba(52,199,89,0.35)"
                    strokeDasharray="4 4"
                  />
                  <line
                    x1={0}
                    x2={innerW}
                    y1={h - BOTTOM_PAD - ((70 - minG) / (maxG - minG)) * (h - TOP_PAD - BOTTOM_PAD)}
                    y2={h - BOTTOM_PAD - ((70 - minG) / (maxG - minG)) * (h - TOP_PAD - BOTTOM_PAD)}
                    stroke="rgba(255,149,0,0.35)"
                    strokeDasharray="4 4"
                  />

                  {fiveMinGridTicks.map((t) => {
                    const x = xAtMs(t);
                    const d = new Date(t);
                    const onHour = d.getMinutes() === 0;
                    return (
                      <line
                        key={`g-${t}`}
                        x1={x}
                        x2={x}
                        y1={TOP_PAD}
                        y2={h - BOTTOM_PAD}
                        stroke="var(--border-light)"
                        strokeWidth={onHour ? 0.75 : 0.45}
                        opacity={onHour ? 0.45 : 0.22}
                      />
                    );
                  })}

                  {labelTicks.map((t) => {
                    const x = xAtMs(t);
                    return (
                      <text
                        key={`l-${t}`}
                        x={x}
                        y={h - 6}
                        className="analytics-svg-label live-glucose-x-label"
                        textAnchor="middle"
                      >
                        {formatTick(t)}
                      </text>
                    );
                  })}

                  {paths.map(
                    (p) =>
                      p.d && (
                        <path
                          key={p.name}
                          d={p.d}
                          fill="none"
                          stroke={p.color}
                          strokeWidth={2}
                          strokeLinejoin="round"
                        />
                      )
                  )}

                  {hoverData && (
                    <g className="live-glucose-tooltip-group">
                      {/* Cursor Line */}
                      <line 
                        x1={hoverData.x} x2={hoverData.x} 
                        y1={TOP_PAD} y2={h - BOTTOM_PAD} 
                        stroke="var(--text-primary)" 
                        strokeWidth="1" 
                        strokeDasharray="4 2" 
                        opacity={0.5} 
                      />
                      
                      {/* Tooltip Background */}
                      <rect 
                        x={hoverData.x + 10 > innerW - 120 ? hoverData.x - 130 : hoverData.x + 10} 
                        y={TOP_PAD + 10} 
                        width={120} 
                        height={30 + hoverData.points.length * 20} 
                        rx={6} 
                        fill="var(--bg-layer2)" 
                        stroke="var(--border-light)" 
                        strokeWidth={1} 
                        filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
                      />
                      
                      {/* Tooltip Content */}
                      <text 
                        x={hoverData.x + 10 > innerW - 120 ? hoverData.x - 120 : hoverData.x + 20} 
                        y={TOP_PAD + 28} 
                        fill="var(--text-secondary)" 
                        fontSize="11" 
                        fontWeight="semibold"
                      >
                        {hoverData.timeMsg}
                      </text>
                      
                      {hoverData.points.map((pt, idx) => (
                        <text 
                          key={pt.name}
                          x={hoverData.x + 10 > innerW - 120 ? hoverData.x - 120 : hoverData.x + 20} 
                          y={TOP_PAD + 48 + idx * 20} 
                          fill={pt.color} 
                          fontSize="13" 
                          fontWeight="bold"
                        >
                          {pt.value} mg/dL
                        </text>
                      ))}
                    </g>
                  )}
                </svg>
              )}
            </div>
          </div>
        </div>
      </div>

      <p className="live-glucose-scale-hint">
        The chart pans only across times with CGM coverage when known (earliest reading per profile). Viewport stays ~{Math.round(LIVE_CHART_PX_PER_FIVE_MIN)}px per 5-minute column; data loads in {CHUNK_MS / 3600000}h slices (sp_glucose_readings).
        {extent.status === 'ready' && extent.globalEarliestMs != null && timelineStartMs > fullStart && (
          <>
            {' '}
            Timeline starts {new Date(timelineStartMs).toLocaleDateString(undefined, { dateStyle: 'medium' })} (selected
            window began {new Date(fullStart).toLocaleDateString(undefined, { dateStyle: 'medium' })}).
          </>
        )}
      </p>

      <div className="agp-compare-legend live-glucose-legend">
        {series.map((s, i) => (
          <span key={`${s.name}-${i}`} className="agp-compare-legend-item">
            <i className="agp-compare-swatch" style={{ background: s.color }} />
            {s.name}
            <span className="live-legend-meta">({s.readings.length} pts loaded)</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export default LiveGlucoseChart;
